import type { ReactNode } from 'react';
import {
  PUBLICATION_LABELS,
  requiresPublicationChrome,
  type LegendaLang,
  type MarkedPublicationState,
  type PublicationState,
} from './vocabulary';

const RIBBON: Record<LegendaLang, Record<MarkedPublicationState, string>> = {
  ru: {
    draft: 'ЧЕРНОВИК · НЕ ДЛЯ ПУБЛИКАЦИИ',
    provisional_only: 'ПРЕДВАРИТЕЛЬНЫЕ ДАННЫЕ · НЕ ДЛЯ ПУБЛИКАЦИИ',
    in_review: 'НА РЕЦЕНЗИИ · НЕ ОПУБЛИКОВАННАЯ ИСТИНА',
    retracted: 'ОТОЗВАНО · НЕ ИСПОЛЬЗОВАТЬ',
    superseded: 'ЗАМЕНЕНО БОЛЕЕ ПОЗДНЕЙ ВЕРСИЕЙ',
  },
  hy: {
    draft: 'ՍԵՎԱԳԻՐ · ՈՉ ՀՐԱՊԱՐԱԿՄԱՆ ՀԱՄԱՐ',
    provisional_only: 'ՆԱԽՆԱԿԱՆ ՏՎՅԱԼՆԵՐ · ՈՉ ՀՐԱՊԱՐԱԿՄԱՆ ՀԱՄԱՐ',
    in_review: 'ՎԵՐԱՆԱՅՄԱՆ ՓՈՒԼՈՒՄ · ՀՐԱՊԱՐԱԿՎԱԾ ՃՇՄԱՐՏՈՒԹՅՈՒՆ ՉԷ',
    retracted: 'ՀԵՏ ԿԱՆՉՎԱԾ · ՉՕԳՏԱԳՈՐԾԵԼ',
    superseded: 'ՓՈԽԱՐԻՆՎԱԾ Է ՆՈՐ ՏԱՐԲԵՐԱԿՈՎ',
  },
  en: {
    draft: 'DRAFT · NOT FOR PUBLICATION',
    provisional_only: 'PROVISIONAL DATA · NOT FOR PUBLICATION',
    in_review: 'IN REVIEW · NOT PUBLISHED TRUTH',
    retracted: 'RETRACTED · DO NOT USE',
    superseded: 'SUPERSEDED BY A LATER VERSION',
  },
};

export interface PublicationBannerProps {
  state: PublicationState;
  lang: LegendaLang;
  /** Dataset version shown in the footer line, e.g. `v1.5.0-rc3`. */
  datasetVersion?: string;
  children: ReactNode;
}

/**
 * Environment chrome for the publication state of everything inside it.
 *
 * Publication is a property of the *scope*, not of any item, so it is carried
 * by the frame rather than by a badge. `published` gets no chrome at all —
 * clean is the signal that this is ground truth, and adding a "verified" ribbon
 * would devalue it.
 *
 * Every other state wraps the content in a 2px hazard border, a ribbon that
 * cannot be scrolled away, and a diagonal watermark tiled across the surface.
 * That redundancy is deliberate: the brief assumes screenshots escape into
 * slide decks, so the marking has to survive a crop. The ribbon repeats its own
 * text for the same reason — a horizontal crop still contains a full phrase.
 */
export default function PublicationBanner({
  state,
  lang,
  datasetVersion,
  children,
}: PublicationBannerProps) {
  if (!requiresPublicationChrome(state)) {
    return <>{children}</>;
  }

  const text = RIBBON[lang][state];

  return (
    <div className="lg-hazard" data-publication-state={state}>
      <div className="lg-hazard__ribbon lg-hatch-hazard">
        <span>{text}</span>
        <span className="lg-hazard__repeat" aria-hidden="true">
          · {text} ·
        </span>
        <span className="lg-hazard__repeat" aria-hidden="true">
          {text} ·
        </span>
        {datasetVersion ? (
          <span style={{ marginLeft: 'auto', letterSpacing: 0 }}>
            {PUBLICATION_LABELS[state][lang]} · {datasetVersion}
          </span>
        ) : null}
      </div>
      {children}
    </div>
  );
}
